import { useEffect, useImperativeHandle, forwardRef } from 'react';

const SCRIPT_BASE = 'https://recaptcha.paybito.com';
const DEFAULT_SITEKEY = 'pbk_live_35de07524591421b574a8831feefaea7';

/**
 * PaybitoCaptchaExchange — loads the Paybito slider captcha script and exposes verify().
 * Calling verify() opens the Paybito slider popup directly (no intermediate UI).
 *
 * Usage (class components):
 *   this.captchaRef = React.createRef();
 *   <PaybitoCaptchaExchange ref={this.captchaRef} />
 *   const { token } = await this.captchaRef.current.verify();
 *
 * Usage (function components):
 *   const captchaRef = useRef();
 *   <PaybitoCaptchaExchange ref={captchaRef} />
 *   const { token } = await captchaRef.current.verify();
 */
const PaybitoCaptchaExchange = forwardRef(function PaybitoCaptchaExchange(
  { sitekey = DEFAULT_SITEKEY },
  ref,
) {
  // Load the Paybito script once per page (idempotent).
  useEffect(() => {
    if (window.paybitoCaptcha) return;
    if (document.querySelector('script[data-paybito-captcha]')) return;

    const s = document.createElement('script');
    s.src = `${SCRIPT_BASE}/v1/api.js`;
    s.async = true;
    s.dataset.paybitoCaptcha = '1';
    document.head.appendChild(s);
    // Intentionally not removed on unmount — lives for the page lifetime.
  }, []);

  // Expose verify() so parent components can await the captcha result.
  useImperativeHandle(ref, () => ({
    verify: () =>
      new Promise((resolve, reject) => {
        if (!window.paybitoCaptcha) {
          return reject(new Error('captcha-loader-not-ready'));
        }
        window.paybitoCaptcha.verify(sitekey, (result) => {
          if (result.success && result.token) {
            //console.log('[PaybitoCaptchaExchange] success response:', result);
            resolve({ token: result.token });
          } else {
            reject(new Error(result.reason || 'captcha-cancelled'));
          }
        });
      }),
  }));

  // Renders nothing — the slider popup is opened by the Paybito script itself.
  return null;
});

export default PaybitoCaptchaExchange;
